/* ============================================
   LeadSidebar — quién es quién en la oportunidad

   Columna derecha de la ficha: los contactos con su
   rol (mismo catálogo que Empresas y el portal) y,
   al pie, la empresa.

   El chat no vive acá de forma permanente: se abre
   sobre esta misma columna al tocar el WhatsApp de un
   contacto, y se vuelve. Antes ocupaba el espacio
   siempre, aunque no hubiera hilo.
   ============================================ */
import { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { llevaPreciosBase } from '@/lib/markup'
import { useApi } from '@/hooks/useApi'
import type { Lead, LeadContacto, RolContacto, Cliente, Cotizacion, CotizacionItemsEstandar } from '@/lib/types'
import { ROLES_CONTACTO } from '@/lib/types'
import { formatCurrency, formatDate, slugRol, r2 } from '@/lib/utils'
import { useAuth } from '@/hooks/useAuth'
import { Badge, type BadgeVariant } from '@/components/ui'
import LeadChatPanel from './LeadChatPanel'
import LeadComercial from './LeadComercial'
import LeadResponsable from './LeadResponsable'
import LeadActividad from './LeadActividad'
import { IconUser, IconMail, IconPhone, IconWhatsApp, IconEdit, IconTrash, IconStar, IconPlus, IconSearch, IconBuilding, IconExternalLink, IconCheck, IconFileText, IconArrowRight, IconEye, IconTag } from '@/components/icons/Icons'

/**
 * Lo que el partner nos debe por una cotización, y en qué moneda.
 *
 * Sale de los ítems: los que tienen `costo_partner` salieron de NUESTRO
 * catálogo. Los demás son suyos y no nos debe nada por ellos.
 */
function costoDeLaCotizacion(cot: Cotizacion): { monto: number; moneda: string } {
  if (!Array.isArray(cot.items)) return { monto: 0, moneda: cot.moneda }
  const conCosto = (cot.items as CotizacionItemsEstandar[]).filter(it => Number(it?.costo_partner) > 0)
  if (conCosto.length === 0) return { monto: 0, moneda: cot.moneda }
  return {
    monto: r2(conCosto.reduce((a, it) => a + Number(it.costo_partner) * (Number(it?.cantidad) || 0), 0)),
    moneda: conCosto[0].costo_partner_moneda || cot.moneda,
  }
}

/**
 * Lo que le queda al partner: lo que cobra MENOS lo que nos paga.
 *
 * Se compara contra el **subtotal**, no contra el total: `costo_partner` es
 * un importe sin IGV, y restarlo del total con IGV regalaba el impuesto como
 * si fuera margen. Una cotización puesta al costo —sin markup todavía—
 * mostraba «Te queda $463.50» cuando lo correcto es cero.
 */
function margenDeLaCotizacion(cot: Cotizacion, costo: number): number {
  const neto = r2(Number(cot.subtotal || 0) - Number(cot.descuento_monto || 0))
  return r2(neto - costo)
}

/** Mismo catalogo que el listado de cotizaciones: un estado se ve igual en los dos lados. */
const ESTADO_COT: Record<string, { label: string; variant: BadgeVariant }> = {
  borrador:       { label: 'Borrador',       variant: 'borrador' },
  enviada:        { label: 'Enviada',        variant: 'enviada' },
  en_negociacion: { label: 'En negociación', variant: 'warning' },
  aceptada:       { label: 'Aceptada',       variant: 'aceptada' },
  rechazada:      { label: 'Rechazada',      variant: 'rechazada' },
}

interface LeadSidebarProps {
  lead: Lead
  onRefresh: () => void
  /** Para la actividad, que las nombra. Las pide la ficha, no este panel. */
  meetings: AgendamientoLead[]
  etapaLabel: string
}

interface AgendamientoLead {
  id: number
  fecha: string
  hora_inicio: string
  hora_fin: string
  google_meet_url?: string | null
  created_at: string
}

interface ContactoBuscado {
  id: number
  nombre: string
  apellido: string | null
  email: string | null
  telefono: string | null
  cargo: string | null
}

const VACIO = { nombre: '', apellido: '', email: '', telefono: '', linkedin: '' }

function iniciales(nombre: string, apellido: string | null): string {
  return `${nombre?.[0] || ''}${apellido?.[0] || ''}`.toUpperCase() || '?'
}

export default function LeadSidebar({ lead, onRefresh, meetings, etapaLabel }: LeadSidebarProps) {
  const api = useApi()
  const navigate = useNavigate()
  // Lo que le queda de cada cotización es suyo y solo suyo: nosotros ya vemos
  // el costo en el editor y en la factura al partner.
  const { isPartner: esPartner } = useAuth()
  // La ficha de la empresa vive en otra ruta según el espacio que monte esto.
  const rutaEmpresas = useLocation().pathname.startsWith('/partner') ? '/partner/empresas' : '/clientes'

  // Chat sobre la columna: null = lista de contactos.
  const [chatContactoId, setChatContactoId] = useState<number | null>(null)

  const [editandoRoles, setEditandoRoles] = useState<number | null>(null)
  const [editandoDatos, setEditandoDatos] = useState<number | null>(null)
  const [datos, setDatos] = useState({ ...VACIO, cargo: '' })

  const [buscando, setBuscando] = useState(false)
  const [consulta, setConsulta] = useState('')
  const [resultados, setResultados] = useState<ContactoBuscado[]>([])
  const [cargandoBusqueda, setCargandoBusqueda] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [creando, setCreando] = useState(false)
  const [nuevo, setNuevo] = useState(VACIO)
  const [guardandoNuevo, setGuardandoNuevo] = useState(false)

  const [empresa, setEmpresa] = useState<Cliente | null>(null)
  const [cotizaciones, setCotizaciones] = useState<Cotizacion[]>([])
  const apiGet = api.get

  // La empresa vinculada se lee aparte: el lead solo guarda el id.
  useEffect(() => {
    let cancelado = false
    if (!lead.cliente_id) { setEmpresa(null); return }
    apiGet<Cliente>(`/api/clientes/${lead.cliente_id}`)
      .then(c => { if (!cancelado) setEmpresa(c) })
      .catch(() => { if (!cancelado) setEmpresa(null) })
    return () => { cancelado = true }
  }, [lead.cliente_id, apiGet])

  // Las cotizaciones de ESTA oportunidad. La etapa del lead decia "Cotizado" y
  // no habia desde donde abrir el documento: estaba en el listado general, a dos
  // pantallas de distancia y sin decir que era el de este trato.
  useEffect(() => {
    let cancelado = false
    apiGet<Cotizacion[]>(`/api/cotizaciones?lead_id=${lead.id}`)
      .then(c => { if (!cancelado) setCotizaciones(Array.isArray(c) ? c : []) })
      .catch(() => { if (!cancelado) setCotizaciones([]) })
    return () => { cancelado = true }
  }, [lead.id, apiGet])

  const contactos = lead.contactos || []

  // ─── Contactos ───

  async function alternarRol(contacto: LeadContacto, rol: RolContacto) {
    const actuales = contacto.roles || []
    const nuevos = actuales.includes(rol)
      ? actuales.filter(r => r !== rol)
      : [...actuales, rol]
    try {
      await api.put(`/api/leads/${lead.id}/contactos/${contacto.contacto_id}`, { roles: nuevos })
      onRefresh()
    } catch (err) { console.error('Error actualizando roles:', err) }
  }

  async function hacerPrincipal(contactoId: number) {
    try {
      await api.put(`/api/leads/${lead.id}/contactos/${contactoId}`, { es_principal: true })
      onRefresh()
    } catch (err) { console.error('Error marcando principal:', err) }
  }

  async function desvincular(contacto: LeadContacto) {
    const nombre = `${contacto.nombre} ${contacto.apellido || ''}`.trim()
    if (!confirm(`¿Desvincular a ${nombre} de esta oportunidad?`)) return
    try {
      await api.del(`/api/leads/${lead.id}/contactos/${contacto.contacto_id}`)
      if (chatContactoId === contacto.contacto_id) setChatContactoId(null)
      onRefresh()
    } catch (err) { console.error('Error desvinculando contacto:', err) }
  }

  function abrirEdicion(c: LeadContacto) {
    setEditandoDatos(c.contacto_id)
    setEditandoRoles(null)
    setDatos({
      nombre: c.nombre || '', apellido: c.apellido || '',
      email: c.email || '', telefono: c.telefono || '', cargo: c.cargo || '',
      linkedin: c.linkedin || '',
    })
  }

  async function guardarDatos(contactoId: number) {
    try {
      await api.put(`/api/contactos/${contactoId}`, {
        nombre: datos.nombre.trim(),
        apellido: datos.apellido.trim() || null,
        email: datos.email.trim() || null,
        telefono: datos.telefono.trim() || null,
        cargo: datos.cargo.trim() || null,
        linkedin: datos.linkedin.trim() || null,
      })
      setEditandoDatos(null)
      onRefresh()
    } catch (err) { console.error('Error guardando contacto:', err) }
  }

  function buscar(texto: string) {
    setConsulta(texto)
    if (timerRef.current) clearTimeout(timerRef.current)
    if (!texto.trim()) { setResultados([]); return }
    timerRef.current = setTimeout(async () => {
      setCargandoBusqueda(true)
      try {
        const res = await api.get<ContactoBuscado[]>(`/api/contactos?search=${encodeURIComponent(texto.trim())}`)
        const yaVinculados = new Set(contactos.map(c => c.contacto_id))
        setResultados(res.filter(r => !yaVinculados.has(r.id)))
      } catch (err) {
        console.error('Error buscando contactos:', err)
        setResultados([])
      } finally { setCargandoBusqueda(false) }
    }, 300)
  }

  async function vincular(contactoId: number) {
    try {
      await api.post(`/api/leads/${lead.id}/contactos`, {
        contacto_id: contactoId,
        es_principal: contactos.length === 0,
      })
      setConsulta(''); setResultados([]); setBuscando(false)
      onRefresh()
    } catch (err) { console.error('Error vinculando contacto:', err) }
  }

  async function crearYVincular() {
    if (!nuevo.nombre.trim()) return
    setGuardandoNuevo(true)
    try {
      const creado = await api.post<{ id: number }>('/api/contactos', {
        nombre: nuevo.nombre.trim(),
        apellido: nuevo.apellido.trim() || null,
        email: nuevo.email.trim() || null,
        telefono: nuevo.telefono.trim() || null,
        linkedin: nuevo.linkedin.trim() || null,
      })
      await api.post(`/api/leads/${lead.id}/contactos`, {
        contacto_id: creado.id,
        es_principal: contactos.length === 0,
      })
      setNuevo(VACIO); setCreando(false)
      onRefresh()
    } catch (err) { console.error('Error creando contacto:', err) }
    finally { setGuardandoNuevo(false) }
  }

  // ─── Chat sobre la columna ───
  if (chatContactoId !== null) {
    return (
      <aside className="ld-sidebar ld-sidebar--chat">
        <LeadChatPanel
          lead={lead}
          soloContactoId={chatContactoId}
          onVolver={() => setChatContactoId(null)}
        />
      </aside>
    )
  }

  return (
    <aside className="ld-sidebar">
      {/* ─── Responsable ───
          Antes que la negociación: una oportunidad que entró sola no tiene
          dueño, y lo primero que hay que resolver no es cuánto vale sino
          quién la agarra. */}
      <LeadResponsable lead={lead} onRefresh={onRefresh} />

      {/* ─── Negociación ───
          Primero de la columna: cuánto vale y para cuándo es lo que se mira de
          reojo mientras se lee el lienzo. En el Overview ocupaba una franja a
          mitad de página, lejos de la cotización con la que se compara. */}
      <LeadComercial lead={lead} onRefresh={onRefresh} />

      {/*
        Empresa va PRIMERO y Contactos después, al revés de como estaba. Con
        Contactos arriba, una oportunidad sin contacto abría con un vacío
        grande y un botón de buscar: lo primero que se leía de la ficha era lo
        que le faltaba. La empresa es de quién es esto; el contacto es un dato
        más de la relación, no el encabezado.
      */}
      {/* ─── Empresa ───
          Una fila y nada más. Antes traía RUC, país, etapa y dirección fiscal:
          cuatro datos que no se miran desde acá —para eso está la ficha, a un
          clic— y que empujaban todo lo demás fuera de la pantalla. La fila
          nombra y lleva; el detalle vive del otro lado de la flecha. */}
      <section className="ld-panel">
        <div className="ld-panel__header">
          <h4 className="ld-panel__titulo"><IconBuilding size={14} /> Empresa</h4>
        </div>

        {empresa ? (
          <button
            type="button"
            className="ld-fila ld-fila--enlace"
            onClick={() => navigate(`${rutaEmpresas}/${empresa.id}`)}
            title="Ver la ficha de la empresa"
          >
            <span className="ld-fila__texto">{empresa.empresa || empresa.razon_social}</span>
            <IconArrowRight size={13} className="ld-fila__flecha" />
          </button>
        ) : lead.empresa ? (
          /* Sin ficha todavía: se nombra igual, pero no lleva a ninguna parte
             —no hay adónde— y se dice en una línea por qué. */
          <div className="ld-fila ld-fila--inerte">
            <span className="ld-fila__texto">{lead.empresa}</span>
            <span className="ld-fila__nota">Aún no es cliente</span>
          </div>
        ) : (
          <p className="ld-panel__vacio-texto">Sin empresa</p>
        )}
      </section>

      {/* ─── Contactos ─── */}
      <section className="ld-panel">
        <div className="ld-panel__header">
          <h4 className="ld-panel__titulo"><IconUser size={14} /> Contactos ({contactos.length})</h4>
          <div className="ld-panel__acciones">
            <button
              type="button"
              className="btn-icon-mini"
              title="Buscar contacto existente"
              onClick={() => { setBuscando(b => !b); setCreando(false) }}
            >
              <IconSearch size={13} />
            </button>
            <button
              type="button"
              className="btn-icon-mini"
              title="Crear contacto nuevo"
              onClick={() => { setCreando(c => !c); setBuscando(false) }}
            >
              <IconPlus size={13} />
            </button>
          </div>
        </div>

        {buscando && (
          <div className="ld-buscador">
            <div className="ld-buscador__input">
              <IconSearch size={13} />
              <input
                type="text"
                className="form-input"
                placeholder="Buscar por nombre o email..."
                value={consulta}
                onChange={e => buscar(e.target.value)}
                autoFocus
              />
            </div>
            {cargandoBusqueda && <p className="ld-buscador__hint">Buscando...</p>}
            {resultados.map(r => (
              <div key={r.id} className="ld-buscador__item">
                <div className="ld-buscador__info">
                  <span className="ld-buscador__nombre">{r.nombre} {r.apellido || ''}</span>
                  {r.email && <span className="ld-buscador__detalle">{r.email}</span>}
                </div>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => vincular(r.id)}>
                  <IconPlus size={12} />
                </button>
              </div>
            ))}
            {consulta.trim() && !cargandoBusqueda && resultados.length === 0 && (
              <p className="ld-buscador__hint">
                Sin resultados.{' '}
                <button type="button" className="btn-link" onClick={() => { setCreando(true); setBuscando(false); setNuevo({ ...VACIO, nombre: consulta }) }}>
                  Crear nuevo →
                </button>
              </p>
            )}
          </div>
        )}

        {creando && (
          <div className="ld-form-mini">
            <div className="ld-form-mini__fila">
              <input className="form-input-mini" placeholder="Nombre *" value={nuevo.nombre} onChange={e => setNuevo(p => ({ ...p, nombre: e.target.value }))} />
              <input className="form-input-mini" placeholder="Apellido" value={nuevo.apellido} onChange={e => setNuevo(p => ({ ...p, apellido: e.target.value }))} />
            </div>
            <div className="ld-form-mini__fila">
              <input className="form-input-mini" placeholder="Email" value={nuevo.email} onChange={e => setNuevo(p => ({ ...p, email: e.target.value }))} />
              <input className="form-input-mini" placeholder="+51..." value={nuevo.telefono} onChange={e => setNuevo(p => ({ ...p, telefono: e.target.value }))} />
            </div>
            <input className="form-input-mini" placeholder="LinkedIn (https://...)" value={nuevo.linkedin} onChange={e => setNuevo(p => ({ ...p, linkedin: e.target.value }))} />
            <div className="ld-form-mini__acciones">
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setCreando(false)}>Cancelar</button>
              <button type="button" className="btn btn-primary btn-sm" onClick={crearYVincular} disabled={guardandoNuevo || !nuevo.nombre.trim()}>
                {guardandoNuevo ? 'Creando...' : 'Crear y vincular'}
              </button>
            </div>
          </div>
        )}

        {/* Una línea, no un bloque centrado con ícono: «Sin contactos» ocupaba
            más alto que tres contactos de verdad, y lo primero que se leía de
            la ficha era lo que le faltaba. */}
        {contactos.length === 0 && !creando && !buscando && (
          <button type="button" className="ld-fila ld-fila--agregar" onClick={() => setBuscando(true)}>
            <IconSearch size={12} />
            <span className="ld-fila__texto">Buscar contacto</span>
          </button>
        )}

        <div className="ld-contactos">
          {contactos.map(c => {
            const nombre = `${c.nombre} ${c.apellido || ''}`.trim()
            const editandoEste = editandoDatos === c.contacto_id

            if (editandoEste) {
              return (
                <div key={c.id} className="ld-contacto ld-contacto--editando">
                  <div className="ld-form-mini__fila">
                    <input className="form-input-mini" placeholder="Nombre" value={datos.nombre} onChange={e => setDatos(p => ({ ...p, nombre: e.target.value }))} />
                    <input className="form-input-mini" placeholder="Apellido" value={datos.apellido} onChange={e => setDatos(p => ({ ...p, apellido: e.target.value }))} />
                  </div>
                  <div className="ld-form-mini__fila">
                    <input className="form-input-mini" placeholder="Email" value={datos.email} onChange={e => setDatos(p => ({ ...p, email: e.target.value }))} />
                    <input className="form-input-mini" placeholder="Teléfono" value={datos.telefono} onChange={e => setDatos(p => ({ ...p, telefono: e.target.value }))} />
                  </div>
                  <input className="form-input-mini" placeholder="Cargo" value={datos.cargo} onChange={e => setDatos(p => ({ ...p, cargo: e.target.value }))} />
                  <input className="form-input-mini" placeholder="LinkedIn (https://...)" value={datos.linkedin} onChange={e => setDatos(p => ({ ...p, linkedin: e.target.value }))} />
                  <div className="ld-form-mini__acciones">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditandoDatos(null)}>Cancelar</button>
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => guardarDatos(c.contacto_id)}>Guardar</button>
                  </div>
                </div>
              )
            }

            return (
              <div key={c.id} className="ld-contacto">
                <div className="ld-contacto__cabecera">
                  <div className="ld-contacto__avatar">{iniciales(c.nombre, c.apellido)}</div>
                  <div className="ld-contacto__identidad">
                    <span className="ld-contacto__nombre">{nombre}</span>
                    {c.cargo && <span className="ld-contacto__cargo">{c.cargo}</span>}
                  </div>
                  <div className="ld-contacto__acciones">
                    {!c.es_principal && (
                      <button type="button" className="btn-icon-mini btn-icon-mini--star" title="Marcar como principal" onClick={() => hacerPrincipal(c.contacto_id)}>
                        <IconStar size={12} />
                      </button>
                    )}
                    <button type="button" className="btn-icon-mini" title="Editar datos" onClick={() => abrirEdicion(c)}>
                      <IconEdit size={12} />
                    </button>
                    <button type="button" className="btn-icon-mini btn-icon-mini--danger" title="Desvincular" onClick={() => desvincular(c)}>
                      <IconTrash size={12} />
                    </button>
                  </div>
                </div>

                <div className="ld-contacto__roles">
                  {c.es_principal && <span className="rol-chip rol-chip--principal">Principal</span>}
                  {(c.roles || []).filter(r => r !== 'PRINCIPAL').map(r => (
                    <span key={r} className={`rol-chip rol-chip--${slugRol(r)}`}>{r}</span>
                  ))}
                  <button
                    type="button"
                    className="rol-chip rol-chip--editar"
                    onClick={() => setEditandoRoles(editandoRoles === c.contacto_id ? null : c.contacto_id)}
                  >
                    {editandoRoles === c.contacto_id ? <><IconCheck size={11} /> Listo</> : <><IconEdit size={10} /> Roles</>}
                  </button>
                </div>

                {editandoRoles === c.contacto_id && (
                  <div className="ld-contacto__roles-editor">
                    {ROLES_CONTACTO.map(rol => {
                      const activo = (c.roles || []).includes(rol)
                      return (
                        <button
                          key={rol}
                          type="button"
                          className={`rol-chip rol-chip--${slugRol(rol)}${activo ? '' : ' rol-chip--off'}`}
                          onClick={() => alternarRol(c, rol)}
                        >
                          {rol}
                        </button>
                      )
                    })}
                  </div>
                )}

                <div className="ld-contacto__datos">
                  {c.email && (
                    <a className="ld-contacto__dato" href={`mailto:${c.email}`}>
                      <IconMail size={11} /> {c.email}
                    </a>
                  )}
                  {c.telefono && (
                    <a className="ld-contacto__dato" href={`tel:${c.telefono}`}>
                      <IconPhone size={11} /> {c.telefono}
                    </a>
                  )}
                  {c.linkedin && (
                    <a className="ld-contacto__dato" href={c.linkedin} target="_blank" rel="noopener noreferrer">
                      <IconExternalLink size={11} /> LinkedIn
                    </a>
                  )}
                </div>

                {c.telefono && (
                  <button
                    type="button"
                    className="ld-contacto__whatsapp"
                    onClick={() => setChatContactoId(c.contacto_id)}
                  >
                    <IconWhatsApp size={14} /> Abrir chat
                  </button>
                )}
              </div>
            )
          })}
        </div>
      </section>

      {/* ─── Cotizaciones ─── */}
      <section className="ld-panel">
        <div className="ld-panel__header">
          <h4 className="ld-panel__titulo"><IconFileText size={14} /> Cotizaciones ({cotizaciones.length})</h4>
        </div>

        {cotizaciones.length === 0 ? (
          <div className="ld-panel__vacio">
            <IconFileText size={20} />
            <p>Sin cotizaciones</p>
          </div>
        ) : (
          <div className="ld-docs">
            {cotizaciones.map(cot => {
              const estado = ESTADO_COT[cot.estado] ?? { label: cot.estado, variant: 'neutral' as BadgeVariant }
              return (
                /*
                  Tarjeta y NO un enlace entero: antes toda la tarjeta abría
                  `/ver/:token` en otra pestaña, así que desde la ficha sólo
                  se podía mirar el documento. Para una cotización que
                  todavía lleva nuestros precios, lo que hace falta es
                  justamente lo contrario — entrar a ponerle el markup—, y un
                  <a> dentro de otro <a> no es HTML válido.
                */
                <div key={cot.id} className="ld-doc">
                  <div className="ld-doc__fila">
                    <span className="ld-doc__numero">{cot.numero}</span>
                    <Badge variant={estado.variant}>{estado.label}</Badge>
                  </div>
                  <div className="ld-doc__fila">
                    <span className="ld-doc__fecha">{formatDate(cot.fecha_emision)}</span>
                    {/* Una propuesta de alcance no lleva importes: mostrar un total
                        que el documento oculta seria contradecirlo. */}
                    <span className="ld-doc__total">
                      {cot.mostrar_precios === false ? '—' : formatCurrency(Number(cot.total || 0), cot.moneda)}
                    </span>
                  </div>
                  {/*
                    Lo que de ese total le queda al partner. El número grande es
                    lo que le cobra a su cliente; sin esta línea, la ficha le
                    dice lo que factura y nunca lo que gana — que es lo que
                    mira una agencia para decidir si el trato vale.

                    Solo cuando hay algo nuestro adentro, y solo si las dos
                    monedas coinciden: restar soles menos dólares da un número
                    que no existe, así que ahí se muestra la deuda a secas.
                  */}
                  {esPartner && cot.mostrar_precios !== false && (() => {
                    const { monto, moneda } = costoDeLaCotizacion(cot)
                    if (monto <= 0) return null
                    return (
                      <div className="ld-doc__neto">
                        {moneda === cot.moneda ? (
                          <>
                            <strong>Te queda {formatCurrency(margenDeLaCotizacion(cot, monto), moneda)}</strong>
                            <span>nos pagas {formatCurrency(monto, moneda)}</span>
                          </>
                        ) : (
                          <span>nos pagas {formatCurrency(monto, moneda)}</span>
                        )}
                      </div>
                    )
                  })()}

                  <div className="ld-doc__acciones">
                    <a
                      className="btn btn-sm btn-secondary"
                      href={`/ver/${cot.public_token}`}
                      target="_blank"
                      rel="noreferrer"
                      title="Abre el documento tal como lo ve el cliente"
                    >
                      <IconEye size={13} /> Ver
                    </a>
                    {/* Sólo mientras lleve NUESTROS precios: una vez puesto el
                        markup, el documento ya es suyo y se edita desde su
                        listado como cualquier otro. */}
                    {esPartner && llevaPreciosBase(cot) && (
                      <Link
                        to={`/partner/cotizaciones/${cot.id}/editar`}
                        className="btn btn-sm btn-primary"
                        title="Poner tu precio sobre nuestro costo"
                      >
                        <IconTag size={13} /> Poner precio
                      </Link>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </section>

      {/* ─── Actividad ───
          Al pie: es historia. En el cuerpo de la ficha empujaba hacia abajo
          todo lo que sí se usa. */}
      <LeadActividad lead={lead} meetings={meetings} etapaLabel={etapaLabel} />
    </aside>
  )
}
